<template>
    <div class="policy-page">
        <div class="container">
            <h1 class="page-title">
                {{ interpolatePolicyText(policy.title, { email: serviceEmail }) }}
            </h1>
            <p class="subsection-text">{{ t('terms.tip2', legalTextParams) }}</p>

            <div class="policy-content">
                <section class="policy-section">
                    <h2 class="section-title">
                        {{ interpolatePolicyText(policy.introTitle, { email: serviceEmail }) }}
                    </h2>
                    <p v-for="(paragraph, introIndex) in policy.intro" :key="`intro-${introIndex}`"
                        class="subsection-text">
                        {{ interpolatePolicyText(paragraph, { email: serviceEmail }) }}
                    </p>
                </section>

                <section v-for="(section, sectionIndex) in policy.sections" :key="`section-${sectionIndex}`"
                    class="policy-section">
                    <h2 class="section-title">
                        {{ interpolatePolicyText(section.title, { email: serviceEmail }) }}
                    </h2>

                    <template v-for="(block, blockIndex) in section.blocks || []"
                        :key="`section-block-${sectionIndex}-${blockIndex}`">
                        <p v-if="block.type === 'paragraph'" class="subsection-text">
                            {{ interpolatePolicyText(getBlockText(block), { email: serviceEmail }) }}
                        </p>
                        <ul v-else-if="block.type === 'list'" class="content-list">
                            <li v-for="(item, itemIndex) in getBlockItems(block)"
                                :key="`section-item-${sectionIndex}-${blockIndex}-${itemIndex}`">
                                {{ interpolatePolicyText(item, { email: serviceEmail }) }}
                            </li>
                        </ul>
                    </template>

                    <div v-for="(subsection, subsectionIndex) in section.subsections || []"
                        :key="`subsection-${sectionIndex}-${subsectionIndex}`" class="subsection">
                        <h3 v-if="subsection.title" class="subsection-title">
                            {{ interpolatePolicyText(subsection.title, { email: serviceEmail }) }}
                        </h3>

                        <template v-for="(block, blockIndex) in subsection.blocks"
                            :key="`subsection-block-${sectionIndex}-${subsectionIndex}-${blockIndex}`">
                            <p v-if="block.type === 'paragraph'" class="content-text">
                                {{ interpolatePolicyText(getBlockText(block), { email: serviceEmail }) }}
                            </p>
                            <ul v-else-if="block.type === 'list'" class="content-list">
                                <li v-for="(item, itemIndex) in getBlockItems(block)"
                                    :key="`subsection-item-${sectionIndex}-${subsectionIndex}-${blockIndex}-${itemIndex}`">
                                    {{ interpolatePolicyText(item, { email: serviceEmail }) }}
                                </li>
                            </ul>
                        </template>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, definePageMeta, setPageLayout, useHead, useRoute } from '#imports'
import { useI18n } from '~/composables/useI18n'
import '@/assets/css/mixins.css'
import { isInApp } from '~/utils/app-bridge'
import acceptableUsePolicyContent from '~/locales/modules/acceptable-use-policy'
import { useLegalEntity } from '~/composables/useLegalEntity'

const { locale, t } = useI18n()
const { legalTextParams } = useLegalEntity()
const route = useRoute()
const serviceEmail = 'help@weryai.com'

const interpolatePolicyText = (
    text?: string,
    variables: Record<string, string> = {}
) => {
    if (!text) return ''

    return Object.entries(variables).reduce((result, [key, value]) => {
        return result.replaceAll(`{${key}}`, value)
    }, text)
}

const policy = computed(() => {
    const fallbackPolicy = acceptableUsePolicyContent.en
    const currentLocale = locale.value as keyof typeof acceptableUsePolicyContent
    const currentPolicy = acceptableUsePolicyContent[currentLocale]

    return currentPolicy?.sections?.length ? currentPolicy : fallbackPolicy
})

const getBlockText = (block: any) => block?.text ?? ''
const getBlockItems = (block: any) => block?.items ?? []

useHead(() => ({
    title: `${policy.value.title} | WeryAI`,
    meta: [
        {
            name: 'description',
            content: policy.value.seoDescription,
        },
    ],
    link: [
        {
            rel: 'canonical',
            href: `https://www.weryai.com${route.path.replace(/^\/en(?=\/)/, '')}`,
        },
    ],
}))

definePageMeta({
    layout: 'website',
})

const isAppEnv = import.meta.client ? isInApp() : false
if (import.meta.client && isAppEnv) {
    setPageLayout('blank')
}
</script>

<style lang="less" scoped>
@import '~/assets/css/policyServe.css';

.subsection-text,
.content-text,
.content-list li {
    white-space: pre-line;
}

.content-list {
    margin-left: 0;
}

.content-list li::before {
    display: none;
}
</style>
